import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useApi } from '@/api/ApiContext';
import type { CloseShiftByIdRequest } from '@/api/handlers/resources/shifts/requests';
import type { ErrorResponse } from '@/api/handlers/responses';
import type { ShiftResponse } from '@/api/models';
import { ShiftQueryKeys } from './shiftQueryKeys';

export type CloseShiftByIdVariables = {
    shiftId: string;
    body: CloseShiftByIdRequest;
};

/**
 * A BranchAdmin closes a shift a seller left open (`POST /shifts/{id}/close`,
 * SEL-31). Unwraps the envelope and throws its `ErrorResponse`, so a refusal
 * reaches the caller as `error.code`. Once it settles it re-reads the register
 * and the shift's ledger summary: on success the row turns closed, and a
 * `SHIFT_ALREADY_CLOSED` or `SHIFT_NOT_FOUND` refusal means the list is stale.
 */
export const useCloseShiftByIdMutation = () => {
    const { apiClient } = useApi();
    const queryClient = useQueryClient();

    return useMutation<ShiftResponse, ErrorResponse, CloseShiftByIdVariables>({
        mutationFn: async ({ shiftId, body }) => {
            const result = await apiClient.shifts.closeById(shiftId, body);
            if (!result.success) {
                throw result.error;
            }
            return result.response;
        },
        onSettled: (_data, _error, { shiftId }) => {
            void queryClient.invalidateQueries({
                queryKey: ShiftQueryKeys.registers(),
            });
            void queryClient.invalidateQueries({
                queryKey: ShiftQueryKeys.ledgerSummaries(shiftId),
            });
        },
    });
};
